import React, { useEffect, useMemo, useRef, useState } from "react";
import { EventCreateStart, CreateMode } from "./EventCreateStart";
import { EventCreateWizard } from "./EventCreateWizard";
import { EventCreateDialog } from "./EventCreateDialog";
import { EventRequestWizard } from "./EventRequestWizard";
import { RequestDraft } from "../../utils/eventRequest";
import { useInviteePicker } from "../../hooks/useInviteePicker";
import {
	ChosenTime,
	DEFAULT_DURATION_MINUTES,
	SEARCH_WINDOW_DAYS,
	useEventScheduling,
} from "../../hooks/useEventScheduling";
import { NewEventPayload } from "../../hooks/useEventManager";
import { toDayKey } from "../../utils/eventDates";
import "./eventManager.css";

interface EventCreateFlowProps {
	open: boolean;
	/** The instant the flow was opened against — a clicked cell, or today. */
	initialDate: string;
	/** Where a drag across the grid finished. Absent for a click. */
	initialEnd?: string;
	/**
	 * The date came from the grid — a double-click or a drag — so it is already
	 * decided.
	 *
	 * Skips straight to the manual dialog. Asking "how would you like to get
	 * started? we'll help you find a time" is the wrong question for someone who
	 * has just pointed at one, and the guided path exists to answer a question
	 * they have already answered.
	 */
	dateKnown?: boolean;
	saving: boolean;
	onClose: () => void;
	onCreate: (payload: NewEventPayload) => Promise<void>;
	/** The request wizard: create, write the answers, resolve to the new id. */
	onRequest: (draft: RequestDraft) => Promise<string | null>;
	onOpenEvent: (id: string) => void;
}

/** Which screen is showing. */
type Screen = "start" | "wizard" | "manual";

/**
 * A local `YYYY-MM-DD`, `days` from today.
 *
 * `toDayKey` rather than `toISOString().slice(0, 10)`, which this used to be
 * and which was wrong for most of the school day: Sydney is 10 or 11 hours
 * ahead of UTC, so until mid-afternoon the UTC date is still YESTERDAY. The
 * window it built therefore opened a day early and closed a day early — asking
 * the availability service for a day that has already been and stopping short
 * of the fortnight the comment below promises.
 */
const plusDays = (days: number) => {
	const d = new Date();
	d.setDate(d.getDate() + days);
	return toDayKey(d);
};

/**
 * The create flow: pick how to start, choose who is going and when, then
 * create.
 *
 * Three screens, one owner. The picker and scheduling state live here rather
 * than in either screen, so going back from the wizard to the mode chooser does
 * not lose who was already selected.
 *
 * The manual screen is the pre-existing quick-create dialog, unchanged. It is
 * reached three ways — choosing "Create manually", "Skip and create manually",
 * or "Create manually instead" in the wizard — because until the availability
 * routes exist it is the only path that completes, and it should never be more
 * than one click away.
 */
/** Which form the manual route opens: the request wizard, or the quick dialog. */
const USE_REQUEST_WIZARD = true;

export const EventCreateFlow: React.FC<EventCreateFlowProps> = ({
	open,
	initialDate,
	initialEnd,
	dateKnown = false,
	saving,
	onClose,
	onCreate,
	onRequest,
	onOpenEvent,
}) => {
	const picker = useInviteePicker();
	const scheduling = useEventScheduling();

	const [screen, setScreen] = useState<Screen>("start");
	const [mode, setMode] = useState<CreateMode>("manual");
	/**
	 * The time that has been chosen, from either route.
	 *
	 * `ChosenTime` rather than `TimeSlot` because a slot picked from a card in
	 * the assistant carries only its start and end — the counts and the label
	 * belong to the list step 2 drew, and a card is not in that list. Those two
	 * fields are also all anything reads: the manual dialog is seeded from them,
	 * and step 2 highlights on `Start`.
	 */
	const [selectedSlot, setSelectedSlot] = useState<ChosenTime | null>(null);

	// Reset on every open. A previous run's invitees reappearing against a
	// different day is the kind of mistake that ends with the wrong cohort
	// invited to something.
	useEffect(() => {
		if (!open) return;
		setScreen(dateKnown ? "manual" : "start");
		// The guided path is the default. It is reachable without the
		// availability routes — only its second step is gated — so this is not
		// pointing people at a dead end.
		setMode("find-time");
		setSelectedSlot(null);
		picker.reset();
		scheduling.reset();
		/* The chat transcript is NOT reset here, and cannot be: ChatKit owns the
		   thread. Reopening the dialog continues the previous conversation, which
		   is wrong for the same reason the invitees are cleared — a discussion
		   about last week's cohort sitting above a fresh event. Fixing it needs
		   the widget's own thread control (`setThreadId(null)`), which is a small
		   follow-up rather than something to bodge from here. */
		// picker/scheduling are stable enough for this to run per open;
		// adding them re-runs the reset on every keystroke and clears the search.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, dateKnown]);

	const startDone = () =>
		setScreen(mode === "manual" ? "manual" : "wizard");

	/**
	 * The wizard hands over to the manual dialog to actually set a time and
	 * title. The invitees chosen in step 1 are not carried into it yet: the
	 * create payload has no attendee field, and attendees are added to the
	 * event after it exists (see the Attendees tab). Once created, the editor
	 * is where they get attached.
	 */
	const toManual = () => setScreen("manual");

	/**
	 * Everyone chosen, as one stable string — the identity of the question the
	 * slot search answers.
	 */
	const inviteeKey = useMemo(
		() => [...picker.groupIds, ...picker.contactIds].join("|"),
		[picker.groupIds, picker.contactIds]
	);
	const lastInviteeKey = useRef(inviteeKey);

	/**
	 * Change who is going and the suggested times stop being an answer.
	 *
	 * They were computed for a different set of people: the percentages, the
	 * "across 62 people" line and the ranking all belong to the old selection,
	 * and a slot that was 100% for one group can be a clash for another. Left on
	 * screen they read as current, and someone would pick one.
	 *
	 * So the slots and any chosen time are cleared and step 2 goes back to
	 * "Suggest times" — an explicit re-ask rather than an automatic refetch,
	 * because the search is the most expensive read in the service and adding
	 * five people one at a time should not fire five of them.
	 *
	 * `reset` is a stable useCallback; the scheduling object itself is a new
	 * literal each render and would fire this effect continuously.
	 */
	const { reset: resetScheduling } = scheduling;
	useEffect(() => {
		if (lastInviteeKey.current === inviteeKey) return;
		lastInviteeKey.current = inviteeKey;
		resetScheduling();
		setSelectedSlot(null);
	}, [inviteeKey, resetScheduling]);

	/**
	 * The window the slot search covers: from today, a fortnight out.
	 *
	 * The API caps this at 31 days. A fortnight is what the create flow needs —
	 * far enough to find something, close enough that the answer is still true
	 * when the timetable shifts.
	 */
	const availabilityQuery = () => ({
		groupIds: picker.groupIds,
		contactIds: picker.contactIds,
		from: plusDays(0),
		to: plusDays(SEARCH_WINDOW_DAYS),
		durationMinutes: DEFAULT_DURATION_MINUTES,
	});

	return (
		<>
			<EventCreateStart
				open={open && screen === "start"}
				mode={mode}
				audiences={picker.audiences}
				onMode={setMode}
				onToggleAudience={picker.toggleAudience}
				onClose={onClose}
				onContinue={startDone}
				onSkipToManual={toManual}
			/>

			<EventCreateWizard
				open={open && screen === "wizard"}
				saving={saving}
				onUseSlot={(start, end) =>
					setSelectedSlot({ Start: start, End: end })
				}
				setTerm={picker.setTerm}
				longEnough={picker.longEnough}
				hasAudience={picker.hasAudience}
				minQueryLength={picker.minQueryLength}
				audiences={picker.audiences}
				onToggleAudience={picker.toggleAudience}
				results={picker.results}
				searching={picker.searching}
				searchFailed={picker.searchFailed}
				picked={picker.picked}
				pickedIds={picker.pickedIds}
				onTogglePick={picker.toggle}
				onRemovePick={picker.remove}
				slots={scheduling.slots}
				slotsLoading={scheduling.searching}
				slotsError={scheduling.error}
				inviteeCount={scheduling.inviteeCount}
				excludedCount={scheduling.excludedCount}
				canLoadMore={scheduling.canLoadMore}
				selectedSlot={selectedSlot}
				onSelectSlot={(slot) =>
					setSelectedSlot({ Start: slot.Start, End: slot.End })
				}
				onFindTimes={() => void scheduling.findTimes(availabilityQuery())}
				// Re-asks with a bigger Limit rather than paging: the list is
				// short and ranked, so a longer one replaces it cleanly.
				onMoreTimes={() =>
					void scheduling.findTimes(
						availabilityQuery(),
						scheduling.limit + scheduling.pageLimit
					)
				}
				onClose={onClose}
				onManual={toManual}
				onContinue={toManual}
			/>

			{/* The request wizard replaced the quick dialog as the manual route.
			    The dialog is kept, not deleted, in case the scope changes: set
			    `USE_REQUEST_WIZARD` to false to bring it back. */}
			{USE_REQUEST_WIZARD ? (
				<EventRequestWizard
					open={open && screen === "manual"}
					initialDate={selectedSlot?.Start ?? initialDate}
					initialEnd={selectedSlot?.End ?? initialEnd}
					saving={saving}
					onClose={onClose}
					onRequest={onRequest}
					onOpenEvent={onOpenEvent}
				/>
			) : (
				<EventCreateDialog
					open={open && screen === "manual"}
					// A slot chosen in step 2 seeds the date and time; otherwise the
					// day the flow was opened against.
					initialDate={selectedSlot?.Start ?? initialDate}
					// A slot chosen in step 2 carries its own end; otherwise the end
					// of a drag, if there was one.
					initialEnd={selectedSlot?.End ?? initialEnd}
					saving={saving}
					onClose={onClose}
					onCreate={onCreate}
				/>
			)}
		</>
	);
};
